import { useState } from "react";
import Papa from "papaparse";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pill } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";

const IGNORE = "__ignore__";
const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");

function MappingStep({ headers, fields, mapping, setMapping }) {
  return (
    <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
      {headers.map((h) => (
        <div key={h} className="grid grid-cols-2 items-center gap-3">
          <span className="truncate font-mono text-xs">{h}</span>
          <Select value={mapping[h] || IGNORE} onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v === IGNORE ? "" : v }))}>
            <SelectTrigger data-testid={`csv-map-${norm(h)}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={IGNORE}>Ignore column</SelectItem>
              {fields.map((f) => <SelectItem key={f.name} value={f.name}>{f.name}{f.required ? " *" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

function Report({ report }) {
  return (
    <div className="space-y-3" data-testid="csv-report">
      <div className="flex flex-wrap gap-2 text-xs">
        <Pill tone="teal">{report.total} rows</Pill><Pill tone="emerald">{report.dry_run ? `${report.valid} ready` : `${report.created} created`}</Pill>
        <Pill tone="amber">{report.duplicates} duplicates</Pill><Pill tone="rose">{report.errors} errors</Pill>
      </div>
      <div className="max-h-[35vh] overflow-y-auto rounded-md border">
        {report.rows.filter((r) => r.status !== "valid" && r.status !== "created").map((r) => (
          <div key={r.row} className="flex gap-3 border-b px-3 py-2 text-xs last:border-0" data-testid={`csv-report-row-${r.row}`}>
            <span className="font-mono text-muted-foreground">Row {r.row}</span>
            <Pill value={r.status === "error" ? "rejected" : "medium"}>{r.status}</Pill>
            <span className="flex-1">{r.label ? `${r.label} — ` : ""}{(r.messages || []).join("; ")}</span>
          </div>
        ))}
        {report.rows.every((r) => r.status === "valid" || r.status === "created") && <p className="p-3 text-xs text-muted-foreground">No problems found.</p>}
      </div>
    </div>
  );
}

export function CsvImportDialog({ entity, label, onDone }) {
  const { api } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [fields, setFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setRows(null); setHeaders([]); setMapping({}); setReport(null); };
  const onFile = async (file) => {
    const { data: fieldList } = await api.get(`/imports/${entity}/fields`);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const hs = res.meta.fields || [];
        setFields(fieldList); setHeaders(hs); setRows(res.data); setReport(null);
        setMapping(Object.fromEntries(hs.map((h) => [h, fieldList.find((f) => norm(f.name) === norm(h))?.name || ""])));
      },
      error: (e) => toast.error(`Could not read CSV: ${e.message}`),
    });
  };
  const run = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${entity}`, { mapping, rows, dry_run: dryRun });
      setReport(data);
      if (!dryRun) { toast.success(`Imported ${data.created} ${label}`); onDone?.(); }
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { reset(); setOpen(true); }} data-testid={`${entity}-import-button`}><FileUp className="mr-1.5 h-4 w-4" />Import CSV</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Import {label} from CSV</DialogTitle>
            <DialogDescription>Map columns, validate and check duplicates before anything is saved.</DialogDescription>
          </DialogHeader>
          {!rows && (
            <label className="flex cursor-pointer flex-col items-start gap-2 rounded-lg border border-dashed p-6 text-sm hover:border-primary/60">
              <span className="font-medium">Choose a .csv file</span>
              <span className="text-xs text-muted-foreground">First row must contain column headers. Up to 5,000 rows.</span>
              <input type="file" accept=".csv,text/csv" className="text-xs" data-testid="csv-file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
            </label>
          )}
          {rows && !report && <><p className="text-xs text-muted-foreground">{rows.length} rows detected. Map each column to a field.</p><MappingStep headers={headers} fields={fields} mapping={mapping} setMapping={setMapping} /></>}
          {report && <Report report={report} />}
          {rows && (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={reset} data-testid="csv-reset-button">Start over</Button>
              {(!report || report.dry_run) && <Button variant="outline" disabled={busy} onClick={() => run(true)} data-testid="csv-validate-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Validate</Button>}
              {report?.dry_run && report.valid > 0 && <Button disabled={busy} onClick={() => run(false)} data-testid="csv-commit-button">Import {report.valid} records</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
